type UsePressHoldHintOptions = {
    clickThreshold?: number;
    clickWindowMs?: number;
    dismissMs?: number;
    maxShowCount?: number;
    storageKey?: string;
};

type UsePressHoldOptions = {
    onTrigger: () => void;
    onStop?: () => void;
    acceleration?: number;
    delay?: number;
    hint?: UsePressHoldHintOptions;
    initialInterval?: number;
    minInterval?: number;
};

type UsePressHoldReturn = {
    isHolding: Ref<boolean>;
    onPointerDown: (e: PointerEvent) => void;
    showHint: Ref<boolean>;
    stop: () => void;
};

const DEFAULT_OPTIONS = {
    acceleration: 0.85,
    delay: 400,
    initialInterval: 200,
    minInterval: 60,
    hint: {
        clickThreshold: 5,
        clickWindowMs: 3000,
        dismissMs: 5000,
        maxShowCount: 5,
        storageKey: 'press_hold_hint',
    },
} as const;

export const usePressHold = (options: UsePressHoldOptions): UsePressHoldReturn => {
    const {
        onTrigger,
        onStop,
        acceleration = DEFAULT_OPTIONS.acceleration,
        delay = DEFAULT_OPTIONS.delay,
        initialInterval = DEFAULT_OPTIONS.initialInterval,
        minInterval = DEFAULT_OPTIONS.minInterval,
    } = options;

    const hint = {
        ...DEFAULT_OPTIONS.hint,
        ...options.hint,
    };

    // Hold state
    const isHolding = ref(false);
    let delayTimer: ReturnType<typeof setTimeout> | null = null;
    let repeatTimer: ReturnType<typeof setTimeout> | null = null;
    let currentInterval = initialInterval;

    // Hint state
    const hintStorage = useLocalStorage<{ holdDone: boolean; showCount: number }>(hint.storageKey, {
        holdDone: false,
        showCount: 0,
    });

    const showHint = ref(false);
    let clickCount = 0;
    let firstClickTime = 0;
    let dismissTimer: ReturnType<typeof setTimeout> | null = null;

    const stopHold = (): void => {
        const wasActive = delayTimer !== null || repeatTimer !== null;

        if (delayTimer !== null) {
            clearTimeout(delayTimer);
            delayTimer = null;
        }

        if (repeatTimer !== null) {
            clearTimeout(repeatTimer);
            repeatTimer = null;
        }

        isHolding.value = false;
        currentInterval = initialInterval;

        if (wasActive) onStop?.();
    };

    const dismissHint = (): void => {
        showHint.value = false;

        if (dismissTimer !== null) {
            clearTimeout(dismissTimer);
            dismissTimer = null;
        }
    };

    const scheduleNextStep = (): void => {
        repeatTimer = setTimeout(() => {
            onTrigger();
            currentInterval = Math.max(currentInterval * acceleration, minInterval);
            scheduleNextStep();
        }, currentInterval);
    };

    const trackClick = (): void => {
        const now = Date.now();

        if (now - firstClickTime > hint.clickWindowMs) {
            clickCount = 1;
            firstClickTime = now;

            return;
        }

        clickCount++;

        if (clickCount >= hint.clickThreshold && !showHint.value) {
            if (hintStorage.value.holdDone || hintStorage.value.showCount >= hint.maxShowCount) return;

            showHint.value = true;
            hintStorage.value.showCount++;

            dismissTimer = setTimeout(() => {
                showHint.value = false;
            }, hint.dismissMs);
        }
    };

    const onPointerDown = (e: PointerEvent): void => {
        if (e.button !== 0) return;

        trackClick();
        onTrigger();

        delayTimer = setTimeout(() => {
            isHolding.value = true;
            scheduleNextStep();
        }, delay);
    };

    watch(isHolding, holding => {
        if (holding) {
            hintStorage.value.holdDone = true;
            dismissHint();
        }
    });

    onUnmounted(() => {
        stopHold();
        dismissHint();
    });

    return {
        isHolding,
        onPointerDown,
        showHint,
        stop: stopHold,
    };
};
